import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { appointmentApi, chatbotApi, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Loader2, CalendarIcon, MoreHorizontal, Check, X, Mail, Phone, RefreshCw } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending", color: "bg-b2-attentionwash text-b2-attention" },
  { value: "confirmed", label: "Confirmed", color: "bg-b2-wash text-b2-washink" },
  { value: "completed", label: "Completed", color: "bg-b2-livewash text-b2-live" },
  { value: "cancelled", label: "Cancelled", color: "bg-black/[0.04] text-b2-muted" }
];

const TABS = [
  { value: "upcoming", label: "Upcoming" },
  { value: "past", label: "Past" }
];

const DASH = "—";

const parseDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

export default function Appointments() {
  const [appointments, setAppointments] = useState([]);
  const [chatbots, setChatbots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterBot, setFilterBot] = useState("all");
  const [filterDate, setFilterDate] = useState(null);
  const [timeframe, setTimeframe] = useState("upcoming");

  // eslint-disable-next-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadData();
  }, [filterStatus, filterBot]);

  const loadData = async () => {
    try {
      const params = {};
      if (filterStatus !== "all") params.status = filterStatus;
      if (filterBot !== "all") params.chatbot_id = filterBot;

      const [apptRes, botsRes] = await Promise.all([
        appointmentApi.list(params),
        chatbotApi.list()
      ]);
      setAppointments(apptRes.data);
      setChatbots(botsRes.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      await appointmentApi.update(id, { status });
      setAppointments(appointments.map(a => a.id === id ? { ...a, status } : a));
      toast.success("Status updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this appointment?")) return;
    try {
      await appointmentApi.delete(id);
      setAppointments(appointments.filter(a => a.id !== id));
      toast.success("Appointment deleted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const getChatbotName = (id) => chatbots.find(b => b.id === id)?.name || "Unknown";
  const getStatusStyle = (status) => STATUS_OPTIONS.find(s => s.value === status)?.color || "bg-black/[0.04] text-b2-muted";
  const getStatusLabel = (status) => STATUS_OPTIONS.find(s => s.value === status)?.label || status || DASH;

  const dateFiltered = filterDate
    ? appointments.filter(a => a.scheduled_at?.startsWith(format(filterDate, "yyyy-MM-dd")))
    : appointments;

  const now = Date.now();
  const isPast = (a) => {
    const d = parseDate(a.scheduled_at);
    return d ? d.getTime() < now : false;
  };

  const upcomingAppointments = dateFiltered.filter(a => !isPast(a));
  const pastAppointments = dateFiltered.filter(isPast);
  const filteredAppointments = timeframe === "past" ? pastAppointments : upcomingAppointments;
  const tabCounts = { upcoming: upcomingAppointments.length, past: pastAppointments.length };

  const filtersActive = filterStatus !== "all" || filterBot !== "all" || Boolean(filterDate);
  const showControls = filteredAppointments.length > 0 || filtersActive;

  if (loading) {
    return (
      <div className="py-24 flex justify-center" data-testid="appointments-page">
        <Loader2 className="w-5 h-5 animate-spin text-b2-faint" />
      </div>
    );
  }

  return (
    <div data-testid="appointments-page">
      {/* -------------------------------- header -------------------------------- */}
      <div className="flex items-start justify-between gap-4 flex-col sm:flex-row sm:items-center">
        <div>
          <h1 className="text-[26px] leading-tight font-bold tracking-[-0.02em] text-b2-ink">Appointments</h1>
          <p className="mt-1.5 text-[14px] text-b2-muted">Booking requests from your assistants.</p>
        </div>
        <button
          type="button"
          onClick={loadData}
          title="Refresh"
          data-testid="refresh-appointments-btn"
          className="h-9 w-9 shrink-0 rounded-[10px] border border-b2-line bg-b2-surface text-b2-muted hover:text-b2-ink flex items-center justify-center transition-colors"
        >
          <RefreshCw className="w-4 h-4" strokeWidth={1.8} />
        </button>
      </div>

      {/* ---------------------------- tabs + filters ---------------------------- */}
      <div className="mt-6 flex flex-wrap items-end gap-x-4 gap-y-2 border-b border-b2-line" data-testid="appointment-tabs">
        <div className="flex items-center gap-5">
          {TABS.map(tab => {
            const active = timeframe === tab.value;
            return (
              <button
                key={tab.value}
                type="button"
                onClick={() => setTimeframe(tab.value)}
                data-testid={`appointments-tab-${tab.value}`}
                className={`-mb-px h-10 border-b-2 text-[13.5px] font-medium transition-colors ${
                  active ? "border-b2-indigo text-b2-ink" : "border-transparent text-b2-muted hover:text-b2-ink"
                }`}
              >
                {tab.label}
                {tabCounts[tab.value] > 0 && (
                  <span className="ml-1.5 tabular-nums opacity-60">{tabCounts[tab.value]}</span>
                )}
              </button>
            );
          })}
        </div>

        {showControls && (
          <div className="ml-auto flex flex-wrap items-center gap-2 pb-2.5">
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="h-9 w-[150px] rounded-[10px] border-b2-line bg-b2-surface text-[13.5px] text-b2-ink">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {STATUS_OPTIONS.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterBot} onValueChange={setFilterBot}>
              <SelectTrigger className="h-9 w-[190px] rounded-[10px] border-b2-line bg-b2-surface text-[13.5px] text-b2-ink">
                <SelectValue placeholder="Assistant" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Assistants</SelectItem>
                {chatbots.map(bot => (
                  <SelectItem key={bot.id} value={bot.id}>{bot.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  data-testid="date-filter-btn"
                  className="h-9 px-3.5 rounded-[10px] border-b2-line bg-b2-surface text-[13.5px] font-medium text-b2-ink hover:bg-black/[0.02]"
                >
                  <CalendarIcon className="w-4 h-4 mr-2 text-b2-muted" strokeWidth={1.8} />
                  {filterDate ? format(filterDate, "PPP") : "Any date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 rounded-[14px] border-b2-line" align="end">
                <Calendar
                  mode="single"
                  selected={filterDate}
                  onSelect={setFilterDate}
                  initialFocus
                />
              </PopoverContent>
            </Popover>

            {filterDate && (
              <button
                type="button"
                onClick={() => setFilterDate(null)}
                className="h-9 px-2.5 rounded-[10px] text-[13px] font-medium text-b2-muted hover:text-b2-ink hover:bg-black/[0.03] transition-colors"
              >
                Clear date
              </button>
            )}
          </div>
        )}
      </div>

      {/* ------------------------------- the list ------------------------------- */}
      {filteredAppointments.length === 0 ? (
        <div className="mt-5 b2-card">
          <div className="px-8 py-24 flex flex-col items-center text-center">
            <CalendarIcon className="w-8 h-8 text-b2-faint" strokeWidth={1.5} />
            <h2 className="mt-5 text-[18px] font-bold tracking-[-0.03em] text-b2-ink">
              Your next booking starts here
            </h2>
            <p className="mt-2 text-[13.5px] text-b2-muted max-w-sm">
              When a visitor requests an appointment, you'll see it here.
            </p>
            <Link
              to="/contact-us"
              className="mt-7 h-9 px-4 rounded-[10px] bg-b2-indigo hover:bg-b2-indigohover text-white text-[13.5px] font-medium inline-flex items-center transition-colors"
              data-testid="setup-bookings-btn"
            >
              Set up bookings
            </Link>
            <Link
              to="/home"
              className="mt-3.5 text-[13px] font-medium text-b2-indigo hover:text-b2-indigohover transition-colors"
              data-testid="bookings-help-link"
            >
              Go to your assistants
            </Link>
          </div>
        </div>
      ) : (
        <div className="mt-5 b2-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-b2-line">
                  <th className="pl-6 pr-4 py-3 text-left text-[12.5px] font-medium text-b2-muted">Date &amp; time</th>
                  <th className="px-4 py-3 text-left text-[12.5px] font-medium text-b2-muted">Visitor</th>
                  <th className="px-4 py-3 text-left text-[12.5px] font-medium text-b2-muted">Contact</th>
                  <th className="px-4 py-3 text-left text-[12.5px] font-medium text-b2-muted">Assistant</th>
                  <th className="px-4 py-3 text-left text-[12.5px] font-medium text-b2-muted">Status</th>
                  <th className="w-16 pl-4 pr-6 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-b2-line">
                {filteredAppointments.map(appt => {
                  const when = parseDate(appt.scheduled_at);
                  return (
                    <tr
                      key={appt.id}
                      className="transition-colors hover:bg-black/[0.015]"
                      data-testid={`appointment-${appt.id}`}
                    >
                      <td className="pl-6 pr-4 py-5 align-middle whitespace-nowrap">
                        <p className="text-[13.5px] font-medium text-b2-ink">
                          {when ? when.toLocaleDateString() : DASH}
                        </p>
                        <p className="mt-0.5 text-[12.5px] text-b2-faint">
                          {when ? when.toLocaleTimeString() : DASH}
                        </p>
                      </td>
                      <td className="px-4 py-5 align-middle">
                        <span className="text-[13.5px] text-b2-ink">{appt.visitor_name || DASH}</span>
                      </td>
                      <td className="px-4 py-5 align-middle">
                        <div className="flex items-center gap-1.5 text-[13px] text-b2-muted">
                          <Mail className="w-3.5 h-3.5 text-b2-faint shrink-0" strokeWidth={1.8} />
                          <span className="truncate">{appt.visitor_email || DASH}</span>
                        </div>
                        {appt.visitor_phone && (
                          <div className="mt-1 flex items-center gap-1.5 text-[13px] text-b2-muted">
                            <Phone className="w-3.5 h-3.5 text-b2-faint shrink-0" strokeWidth={1.8} />
                            <span className="truncate">{appt.visitor_phone}</span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-5 align-middle">
                        <span className="text-[13.5px] text-b2-inksoft">{getChatbotName(appt.chatbot_id)}</span>
                      </td>
                      <td className="px-4 py-5 align-middle">
                        <span
                          className={`inline-flex items-center h-6 px-2.5 rounded-full text-[12px] font-medium ${getStatusStyle(appt.status)}`}
                        >
                          {getStatusLabel(appt.status)}
                        </span>
                      </td>
                      <td className="pl-4 pr-6 py-5 align-middle text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 rounded-[10px] text-b2-faint hover:text-b2-ink hover:bg-black/[0.04]"
                              data-testid={`appointment-menu-${appt.id}`}
                            >
                              <MoreHorizontal className="w-4 h-4" strokeWidth={1.8} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="rounded-[10px] border-b2-line">
                            <DropdownMenuItem
                              className="text-[13.5px] text-b2-ink"
                              onClick={() => handleUpdateStatus(appt.id, "confirmed")}
                            >
                              <Check className="w-4 h-4 mr-2 text-b2-live" strokeWidth={1.8} /> Confirm
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-[13.5px] text-b2-ink"
                              onClick={() => handleUpdateStatus(appt.id, "completed")}
                            >
                              <Check className="w-4 h-4 mr-2 text-b2-muted" strokeWidth={1.8} /> Mark complete
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-[13.5px] text-b2-ink"
                              onClick={() => handleUpdateStatus(appt.id, "cancelled")}
                            >
                              <X className="w-4 h-4 mr-2 text-b2-muted" strokeWidth={1.8} /> Cancel
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-[13.5px] text-red-600 focus:text-red-600 focus:bg-red-50"
                              onClick={() => handleDelete(appt.id)}
                            >
                              <X className="w-4 h-4 mr-2" strokeWidth={1.8} /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
